/**
 * Number base converter — pure, deterministic conversion via BigInt
 * (so arbitrarily large numbers stay exact). Safe to render server-side.
 */

export type Base = 2 | 8 | 10 | 16;

const PATTERNS: Record<Base, RegExp> = {
  2: /^-?[01]+$/,
  8: /^-?[0-7]+$/,
  10: /^-?[0-9]+$/,
  16: /^-?[0-9a-fA-F]+$/,
};

const DIGITS = "0123456789abcdef";

/** Parse a string written in `base` into a BigInt, or null if invalid. */
export function parseInBase(str: string, base: Base): bigint | null {
  const s = str.trim();
  if (!Object.hasOwn(PATTERNS, base) || !PATTERNS[base].test(s)) return null;
  const neg = s.startsWith("-");
  const body = (neg ? s.slice(1) : s).toLowerCase();
  const b = BigInt(base);
  let acc = 0n;
  for (const ch of body) {
    acc = acc * b + BigInt(DIGITS.indexOf(ch));
  }
  return neg ? -acc : acc;
}

/** Format a BigInt in `base` (hex uppercased). */
export function toBase(value: bigint, base: Base): string {
  if (value === 0n) return "0";
  const neg = value < 0n;
  let v = neg ? -value : value;
  const b = BigInt(base);
  let out = "";
  while (v > 0n) {
    out = DIGITS[Number(v % b)] + out;
    v /= b;
  }
  if (base === 16) out = out.toUpperCase();
  return neg ? `-${out}` : out;
}

export interface Conversions {
  bin: string;
  oct: string;
  dec: string;
  hex: string;
}

export type ConvertResult = ({ ok: true } & Conversions) | { ok: false };

export function convertAll(str: string, fromBase: Base): ConvertResult {
  const value = parseInBase(str, fromBase);
  if (value === null) return { ok: false };
  return {
    ok: true,
    bin: toBase(value, 2),
    oct: toBase(value, 8),
    dec: toBase(value, 10),
    hex: toBase(value, 16),
  };
}
